import { Button } from "react-bootstrap";
import { ImCross, ImNext } from "react-icons/im";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { Question } from "../../types";
import { getSjtCorrectOrderIds } from "../../components/SJT/sjtUtils";
import { useEffect } from "react";

const PracticePageSidebar = (props: {
  questions: Question[];
  submittedQuestionIds: Set<number>;
  selectedAnswers: {
    [key: number]: number;
  };
  // For SJT questions, ordered answer option ids by question id
  sjtOrderedAnswers?: {
    [key: number]: number[];
  };
  // For RolePlay questions, track if answered correctly
  rolePlayAnswers?: {
    [key: number]: boolean;
  };
  maxSeenQuestionIndex: number;
  currentQuestionIndex: number;
  onQuestionClicked: (index: number) => void;
  totalQuestionCount?: number;
  isLoadingMore?: boolean;
  sessionLimitReached?: boolean;
  sessionQuestionLimit?: number;
}) => {
  useEffect(() => {
    const element = document.getElementById(`q${props.currentQuestionIndex + 1}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [props.currentQuestionIndex]);

  const questions = props.questions.slice(0, props.maxSeenQuestionIndex + 1);
  const isSjtQuestion = (q?: Question) => (
    q?.type?.toLowerCase?.() === 'sjt' ||
    (Array.isArray(q?.answerOptions) && q!.answerOptions.some(a => typeof a.correctRank === 'number'))
  );
  const isRolePlayQuestion = (q?: Question) => q?.type?.toLowerCase?.() === 'role_play';

  const isQuestionCorrect = (q: Question) => {
    if (isRolePlayQuestion(q)) {
      return props.rolePlayAnswers?.[q.id] === true;
    }
    if (isSjtQuestion(q)) {
      const studentOrder = props.sjtOrderedAnswers?.[q.id];
      const correctOrder = getSjtCorrectOrderIds(q) || [];
      if (!studentOrder || studentOrder.length !== correctOrder.length) return false;
      return studentOrder.every((id, i) => id === correctOrder[i]);
    }
    const selectedAnswerOptionId = props.selectedAnswers[q.id];
    const correctAnswerOptionId = q.answerOptions.filter((answer) => answer.isCorrect)[0].id;
    return selectedAnswerOptionId === correctAnswerOptionId;
  };

  const numberCorrect = Array.from(props.submittedQuestionIds).filter((questionId) => {
    const q = questions.find((question) => question.id === questionId);
    if (!q) return false;
    return isQuestionCorrect(q);
  }).length;

  const numberIncorrect = props.submittedQuestionIds.size - numberCorrect;

  const percentage =
    Math.round((numberCorrect / props.submittedQuestionIds.size) * 100) || 0;

  const questionButtons = questions.map((question, index) => {
    const isViewingThisQuestion = props.currentQuestionIndex === index;
    const questionNumber = index + 1;
    if (!props.submittedQuestionIds.has(question.id)) {
      return (
        <li style={{ marginBottom: index === questions.length - 1 ? 40 : undefined }}>
          <Button
            id={`q${questionNumber}`}
            style={{
              fontWeight: isViewingThisQuestion ? "bold" : "normal",
              fontSize: isViewingThisQuestion ? "larger" : "small",
              transition: "all 0.3s ease",
              boxShadow: isViewingThisQuestion
                ? "0 1px 10px rgba(0, 0, 0, 0.1)"
                : undefined,
            }}
            onClick={(e) => {
              props.onQuestionClicked(index);
            }}
            size="sm"
            variant={isViewingThisQuestion ? "light" : "light"}
          >
            <ImNext size={isViewingThisQuestion ? 16 : 14} /> Q{questionNumber}
          </Button>
        </li>
      );
    }

    const isCorrect = isQuestionCorrect(question);

    if (isCorrect) {
      return (
        <li style={{ marginBottom: index === questions.length - 1 ? 20 : undefined }}>
          <Button
            id={`q${questionNumber}`}
            style={{
              fontWeight: isViewingThisQuestion ? "bold" : "normal",
              fontSize: isViewingThisQuestion ? "larger" : "small",
              transition: "all 0.3s ease",
              boxShadow: isViewingThisQuestion
                ? "0 1px 10px rgba(0, 0, 0, 0.1)"
                : undefined,
            }}
            onClick={(e) => {
              props.onQuestionClicked(index);
            }}
            size="sm"
            variant={isViewingThisQuestion ? "light" : "light"}
          >
            <IoIosCheckmarkCircle
              size={isViewingThisQuestion ? 18 : 16}
              color="green"
            />{" "}
            Q{questionNumber}
          </Button>
        </li>
      );
    } else {
      return (
        <li style={{ marginBottom: index === questions.length - 1 ? 20 : undefined }}>
          <Button
            id={`q${questionNumber}`}
            style={{
              fontWeight: isViewingThisQuestion ? "bold" : "normal",
              fontSize: isViewingThisQuestion ? "larger" : "small",
              transition: "all 0.3s ease",
              boxShadow: isViewingThisQuestion
                ? "0 1px 10px rgba(0, 0, 0, 0.1)"
                : undefined,
            }}
            onClick={(e) => {
              props.onQuestionClicked(index);
            }}
            size="sm"
            variant={isViewingThisQuestion ? "light" : "light"}
          >
            <ImCross
              color="white"
              size={isViewingThisQuestion ? 15 : 14}
              style={{
                backgroundColor: "red",
                // color: isViewingThisQuestion ? "red" : "white",
                borderRadius: "10px",
                padding: "3px",
                marginRight: "5px",
              }}
            />
            Q{questionNumber}
          </Button>
        </li>
      );
    }
  });

  return (
    <div className="questions-progress-tracker">
      <div className="sticky-top-part">
        <h5 className="mb-4">
          Score <span style={{ float: "right" }}>{percentage}%</span>
        </h5>
        <ul>
          <li>
            <IoIosCheckmarkCircle color="green" size={20} /> Correct{" "}
            <span className="progress-total-correct">{numberCorrect}</span>
          </li>
          <li>
            <ImCross
              color="white"
              size={18}
              style={{
                backgroundColor: "red",
                borderRadius: "10px",
                padding: "5px",
              }}
            />{" "}
            Incorrect{" "}
            <span className="progress-total-incorrect">{numberIncorrect}</span>
          </li>
        </ul>
      </div>

      <div className="past-questions-tracker">
        <ul>{questionButtons}</ul>

        {props.isLoadingMore ? (
          <p style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>Loading more...</p>
        ) : null}

        {props.totalQuestionCount !== undefined &&
        props.totalQuestionCount > props.questions.length ? (
          <p style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
            Showing {props.questions.length} of {props.totalQuestionCount}
          </p>
        ) : null}

        {props.sessionLimitReached ? (
          <p style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
            Session limit reached ({props.sessionQuestionLimit ?? 50} questions).
            {props.totalQuestionCount === undefined
              ? " Answer some questions to load more."
              : " Review these before loading more."}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default PracticePageSidebar;
